import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { NP_API_URL, NP_SEARCH_DEBOUNCE_MS } from '../../utils/novaPostApi';
import { useNovaPostDeliveryCityRef } from '../../hooks/useNovaPostDeliveryCityRef';

const WAREHOUSES_LIMIT = '500';

const NovaPostDepartmentInput = ({
  value,
  onDepartmentChange,
  novaPostCityRef,
  novaPostDeliveryCityRef,
  cityName,
}) => {
  const [departmentSuggestions, setDepartmentSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const departmentRef = useRef();
  const wrapperRef = useRef(null);
  const searchTimerRef = useRef(null);
  const searchRequestIdRef = useRef(0);

  const { cityRef, isResolving } = useNovaPostDeliveryCityRef({
    cityName,
    novaPostCityRef,
    novaPostDeliveryCityRef,
  });

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchRequestIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchRequestIdRef.current += 1;
    setShowInput(false);
    if (cityRef) {
      setDepartmentSuggestions([]);
      setFilteredSuggestions([]);
      setSearchQuery('');
      setIsLoading(true);
      searchDepartments('');
    } else {
      setDepartmentSuggestions([]);
      setFilteredSuggestions([]);
      setIsLoading(false);
    }
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchRequestIdRef.current += 1;
    };
  }, [cityRef]);

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowInput(false);
    }
  };

  const searchDepartments = (query) => {
    if (!cityRef) {
      setIsLoading(false);
      return;
    }

    const requestId = ++searchRequestIdRef.current;

    axios
      .post(NP_API_URL, {
        modelName: 'AddressGeneral',
        calledMethod: 'getWarehouses',
        methodProperties: {
          CityRef: cityRef,
          FindByString: query || '',
          Limit: WAREHOUSES_LIMIT,
          Page: '1'
        }
      })
      .then((response) => {
        if (requestId !== searchRequestIdRef.current) return;
        if (response.data.success) {
          const departments = (response.data.data || []).map((department) => ({
            description: department.Description,
            ref: department.Ref
          }));
          setDepartmentSuggestions(departments);
          setFilteredSuggestions(departments);
        } else {
          setDepartmentSuggestions([]);
          setFilteredSuggestions([]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        if (requestId !== searchRequestIdRef.current) return;
        console.error('Error fetching departments:', error);
        setDepartmentSuggestions([]);
        setFilteredSuggestions([]);
        setIsLoading(false);
      });
  };

  const handleDepartmentChange = (e) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchRequestIdRef.current += 1;
    setIsLoading(false);

    if (inputValue.trim().length >= 2) {
      setFilteredSuggestions([]);
      searchTimerRef.current = setTimeout(() => searchDepartments(inputValue.trim()), NP_SEARCH_DEBOUNCE_MS);
      return;
    }

    if (inputValue.trim().length === 0) {
      setFilteredSuggestions(departmentSuggestions);
      return;
    }

    const localFiltered = departmentSuggestions.filter(department =>
      department.description.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredSuggestions(localFiltered);
  };

  const handleDepartmentSuggestionClick = (suggestion) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchRequestIdRef.current += 1;
    setIsLoading(false);
    onDepartmentChange(suggestion);
    setShowInput(false);
    setSearchQuery('');
  };

  const isBusy = isLoading || isResolving;

  const buttonLabel = isBusy
    ? 'Завантаження...'
    : (value || (!cityRef ? 'Спочатку оберіть місто' : 'Оберіть відділення / поштомат'));

  return (
    <div className='mb-3' ref={wrapperRef}>
      <label htmlFor='departmentNumber'>Номер відділення / поштомату</label>
      <div className='dropdown' onClick={() => !isBusy && setShowInput(!showInput && cityRef)}>
        <button
          className='form-control text-left overflow-auto'
          type='button'
          style={{
            cursor: 'pointer',
            backgroundColor: cityRef ? '#fff' : '#e9ecef',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            height: 'auto'
          }}
          disabled={!cityRef || isBusy}
        >
          {buttonLabel}
          <span className='float-right'>
            <i className='bi bi-chevron-down'></i>
          </span>
        </button>
      </div>
      {showInput ? (
        <div>
          <div className='input-group'>
            <div className='input-group-prepend'>
              <span className='input-group-text'><i className='bi bi-search'></i></span>
            </div>
            <input
              ref={departmentRef}
              type='search'
              className='form-control'
              placeholder='Пошук відділення чи поштомату (мін. 2 символи)'
              value={searchQuery}
              onChange={handleDepartmentChange}
              autoComplete='new-password'
            />
          </div>

          <ul className='list-group' style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.9rem' }}>
            {filteredSuggestions.length === 0 ? (
              <li className='list-group-item text-muted small'>
                {searchQuery.trim().length >= 2 ? 'Нічого не знайдено' : 'Введіть номер або адресу для пошуку'}
              </li>
            ) : (
              filteredSuggestions.map((suggestion) => (
                <li
                  key={suggestion.ref}
                  className='list-group-item'
                  onClick={() => handleDepartmentSuggestionClick(suggestion)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                >
                  {suggestion.description}
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default NovaPostDepartmentInput;
